import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { nanoid } from "nanoid";

/* ================= TYPES ================= */
type DialogVariant = "sheet" | "modal";


type DialogItem = {
  id: string;
  content: ReactNode;
  header?: ReactNode;
  route?: string;
  variant?: DialogVariant;
  fullScreen?: boolean;       // <--- new property
  initialSnap?: number;
  closeOnBackdrop?: boolean;
};

type DialogContextType = {
  open: (opts: Omit<DialogItem, "id">) => string;
  close: (id?: string) => void;
  closeAll: () => void;
};

/* ================= CONFIG ================= */
const SNAP_POINTS = [0.25, 0.5, 0.75];
const DEFAULT_SNAP = 0.85;

/* ================= CONTEXT ================= */
const DialogContext = createContext<DialogContextType | null>(null);

/* ================= PROVIDER ================= */
export const MyDialogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [stack, setStack] = useState<DialogItem[]>([]);
  const navigate = useNavigate();
  const lastFocus = useRef<HTMLElement | null>(null);

  const open = useCallback(
    ({
      content,
      header,
      route,
      variant = "sheet",
      fullScreen,
      initialSnap,
      closeOnBackdrop = true,
    }: Omit<DialogItem, "id">) => {
      const id = nanoid();
      lastFocus.current = document.activeElement as HTMLElement;

      setStack((s) => [
        ...s,
        {
          id,
          content,
          header,
          route,
          variant,
          fullScreen,
          initialSnap,
          closeOnBackdrop,
        },
      ]);

      if (route) navigate(route);
      return id;
    },
    [navigate]
  );

  const close = useCallback(
    (id?: string) => {
      setStack((s) => {
        if (!s.length) return s;

        const next = id ? s.filter((d) => d.id !== id) : s.slice(0, -1);
        const top = next.at(-1);

        // Navigate only if top has a route
        if (top?.route) navigate(top.route, { replace: true });
        if (!next.length) lastFocus.current?.focus();

        return next;
      });
    },
    [navigate]
  );

  const closeAll = useCallback(() => {
    setStack([]);
    //navigate("/", { replace: true });   // optional: reset route
    lastFocus.current?.focus();
  }, [navigate]);

  /* ---------- ESC ---------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  /* ---------- BODY LOCK ---------- */
  useEffect(() => {
    const hasSheet = stack.some(d => d.variant !== "modal");
    document.body.style.overflow = hasSheet ? "hidden" : "";

  }, [stack.length]);

  return (
    <DialogContext.Provider value={{ open, close, closeAll }}>
      {children}

      {createPortal(
        <AnimatePresence>
          {stack.map((dlg, index) =>
            dlg.variant === "modal" ? (
              <CenteredDialog
                key={dlg.id}
                data={dlg}
                isTop={index === stack.length - 1}
                zIndex={1000 + index}
                onClose={() => close(dlg.id)}
              />
            ) : (
              <DialogSheet
                key={dlg.id}
                data={dlg}
                isTop={index === stack.length - 1}
                zIndex={1000 + index}
                onClose={() => close(dlg.id)}
              />
            )
          )}
        </AnimatePresence>,
        getDialogRoot()
      )}
    </DialogContext.Provider>
  );
};


const DialogSheet = ({
  data,
  isTop,
  zIndex,
  onClose,
}: {
  data: DialogItem;
  isTop: boolean;
  zIndex: number;
  onClose: () => void;
}) => {
  const dragControls = useDragControls();

  const [snap, setSnap] = useState(
    data.fullScreen ? 0.9 : data.initialSnap ?? DEFAULT_SNAP
  );

  const windowHeight = window.innerHeight;

  return (
    <motion.div
      className="fixed inset-0 flex items-end justify-center"
      style={{ zIndex }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      {isTop && (
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={data.closeOnBackdrop ? onClose : undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}

      {/* Bottom Sheet */}
      <motion.div
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: windowHeight }}
        dragElastic={0.2}
        onDragEnd={(_, info) => {
          if (info.offset.y > 160 || info.velocity.y > 900) onClose();
          const draggedHeight = windowHeight - info.point.y;
          setSnap(Math.min(0.95, Math.max(0.25, draggedHeight / windowHeight)));
        }}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        style={{
          height: `${snap * 100}vh`,
          width: "100%",
          maxWidth: data.fullScreen ? "100%" : "min(95vw, 900px)",
        }}
        className={`
    absolute bottom-0
    bg-white dark:bg-gray-900
    shadow-2xl
    flex flex-col
    overflow-hidden
    ${data.fullScreen ? "rounded-none" : "rounded-t-2xl"}
  `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        <div
          className="flex justify-center py-3 cursor-grab active:cursor-grabbing flex-shrink-0"
          onPointerDown={(e) => dragControls.start(e)}
        >
          <div className="w-12 h-1.5 bg-gray-400 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        {data.header && (
          <div
            className="px-4 h-[52px] flex items-center justify-between border-b border-gray-200 dark:border-gray-700 flex-shrink-0"
            onPointerDown={(e) => dragControls.start(e)}
          >
            <div className="font-semibold truncate">{data.header}</div>
            <button onClick={onClose} className="text-gray-600 dark:text-gray-300">
              ✕
            </button>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 touch-pan-y">
          {data.content}
        </div>
      </motion.div>
    </motion.div>
  );
};

const CenteredDialog = ({
  data,
  isTop,
  zIndex,
  onClose,
}: {
  data: DialogItem;
  isTop: boolean;
  zIndex: number;
  onClose: () => void;
}) => {
  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      {isTop && (
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={data.closeOnBackdrop ? onClose : undefined}
        />
      )}

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.90, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 24 }}
        className="
    relative z-10
    w-full max-w-3xl
    max-h-[90vh]        /* <-- prevent modal overflow */
    bg-white dark:bg-gray-900
    rounded-2xl shadow-2xl
    overflow-hidden flex flex-col
  "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {data.header && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 font-semibold flex-shrink-0">
            {data.header}
          </div>
        )}

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {data.content}
        </div>

        {/* Optional Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ================= ROOT ================= */
function getDialogRoot() {
  let el = document.getElementById("dialog-root");
  if (!el) {
    el = document.createElement("div");
    el.id = "dialog-root";
    document.body.appendChild(el);
  }
  return el;
}

/* ================= HOOK ================= */
export const useMyDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useMyDialog must be used inside MyDialogProvider");
  return ctx;
};